import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IMenuItem } from '@src/slide/application/service/menu-manager/menu-items/menu-items.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { isMediaContent } from '@src/slide/domain/ui-model/elements/element-judge';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { isMac, isWeChat, isWxMiniProgram } from '@src/slide/infrastructure/module/independent/agent/common';
import { filterArrayWithIds } from '@editor/src/js/constants/data-utils';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { needHideOcrEntry } from '@src/slide/application/service/menu-manager/menu-items/right';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import * as FloatBarMenu from '@editor/src/js/constants/float-toobbar-data';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import { getAllImage } from '../menu-values/element-menu-values-getter';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { BaseEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-services';

/**
 * 获取快捷菜单
 * @param model uiModel
 */
export function getQuickSettingMenuItems(model: IBaseModel | INoteModel): IMenuItem[] {
    const modelType = model.type;
    // 多选元素
    if ([UIModelType.MULTIPLY].includes(modelType)) {
        return getMultiQuickSettingMenuItems(model as IMultiplyModel);
    }
    // 组合
    if ([UIModelType.GROUP].includes(modelType)) {
        return getGroupQuickSettingMenuItems(model as IGroupModel);
    }
    // 表格
    if ([UIModelType.TABLE].includes(modelType)) {
        return getTableQuickSettingMenuItems(model as ITableModel);
    }
    // 图片
    if ([UIModelType.IMAGE].includes(modelType)) {
        return getImageQuickSettingMenuItems(model as IPictureModel);
    }
    // 形状
    if ([UIModelType.SHAPE].includes(modelType)) {
        return getShapeQuickSettingMenuItems(model as IShapeModel);
    }
    // 图表
    if ([UIModelType.CHART].includes(modelType)) {
        return getChartQuickSettingMenuItems(model as IChartModel);
    }
    // SmartArt
    if ([UIModelType.SMART_ART].includes(modelType)) {
        return getSmartArtQuickSettingMenuItems(model as ISmartArtModel);
    }
    return [];
}

/**
 * 获取图片快捷菜单
 * @param model uiModel
 */
// eslint-disable-next-line complexity
function getImageQuickSettingMenuItems(model: IPictureModel): IMenuItem[] {
    // 如果此图片为公式图片，返回公式图片快捷工具
    if (model.props.addon && model.props.addon.type === 'latex') {
        const { aid } = model.props.addon;
        const addonQuickItems = (getAddonManager().addonHandler as IAddonHandler).addonQuickItemsMap?.get(aid)
            || ['editAddon'];
        return FloatBarMenu.LatexQuickItems(addonQuickItems);
    }
    // 如果是音视频，返回对应工具栏
    if ((model as IPictureModel).isMedia?.()) {
        if (isMac() && (isWeChat() || isWxMiniProgram())) {
            return filterArrayWithIds(
                FloatBarMenu.attachmentCommonItems,
                [ActionType.DOWNLOAD_ATTACHMENT, ActionType.DIVIDER],
            );
        }
        return FloatBarMenu.attachmentCommonItems;
    }
    const addonMenuItems = FloatBarMenu.ImageQuickItems.find(item => item.id === ActionType.ADDON);
    if (addonMenuItems?.dropdown) {
        const {
            addonSceneItemsMap,
            formatAddonSceneList,
            addonScenes,
        } = getAddonManager().addonHandler as IAddonHandler;
        addonMenuItems.dropdown.data = formatAddonSceneList(addonSceneItemsMap.get(addonScenes.IMAGE), true);
    }

    let imageQuickItems = FloatBarMenu.ImageQuickItems.slice();
    if (needHideOcrEntry(model)) {
        imageQuickItems = imageQuickItems.filter(item => item.id !== ActionType.OCR_PICTURE);
    }

    // 是否显示AI的配图功能（联网搜图和重新生图）
    if (getAIController()?.checkIsSupportAI()) {
        // 是否显示联网搜图
        const index = imageQuickItems.findIndex(item => item.id === ActionType.REPLACE_PICTURE);
        if (index >= 0) {
            imageQuickItems.splice(index, 1, FloatBarMenu.ImageReplaceWithAI);
        }

        const descr = model.sdkData?.getDescription();
        if (descr && descr.type === 'image' && !descr.sourceUrl) {
            imageQuickItems = imageQuickItems.concat(FloatBarMenu.generateImageItems);
        } else {
            imageQuickItems = imageQuickItems.concat(FloatBarMenu.generateImageLiteItems);
        }
    }

    return imageQuickItems;
}

/**
 * 获取形状快捷菜单
 * @param model uiModel
 */
// eslint-disable-next-line complexity
function getShapeQuickSettingMenuItems(model: IShapeModel): IMenuItem[] {
    // 链接卡片态不显示快捷工具栏
    const isIframe = model.props?.addon?.displayMode === AddonMode.IFRAME || false;
    if (isIframe) {
        return [];
    }
    if (model.isAttachment()) {
        if (model.isAttachmentUploaded()) {
            return FloatBarMenu.attachmentCommonItems;
        }
        return []; // 下载相关
    }

    // 如果此Shape为导入的公式图片，则返回公式图片的快捷工具
    // UPDATE：识别公式入口暂时下掉，返回普通形状的工具栏
    if (model.isTextMath) {
        return FloatBarMenu.ShapeQuickItems;
    }

    if (model.editorInstance?.isExpanded()) {
        return FloatBarMenu.TextQuickItems;
    }


    // 是否显示独立的AI的配图功能（联网搜图和重新生图）
    const fillType = (model as IElementBaseModel).getBackgroundFill?.().getFillType();
    if (fillType === FillType.ImageFill) {
        const items = FloatBarMenu.ImageFillQuickItems.slice();

        if (getAIController()?.checkIsSupportAI()) {
            // 是否显示联网搜图
            const index = items.findIndex(item => item.id === ActionType.REPLACE_PICTURE);
            if (index >= 0) {
                items.splice(index, 1, FloatBarMenu.ImageReplaceWithAI);
            }

            const descr = model.sdkData?.getDescription();
            if (descr && descr.type === 'image' && !descr.sourceUrl) {
                return items.concat(FloatBarMenu.generateImageItems);
            }
            return items.concat(FloatBarMenu.generateImageLiteItems);
        }

        return items;
    }

    return FloatBarMenu.ShapeQuickItems;
}

/**
 * 获取表格快捷菜单
 * @param model uiModel
 */
function getTableQuickSettingMenuItems(model: ITableModel): IMenuItem[] {
    const editElement = model.getEditingElement();
    if (editElement?.isExpanded()) {
        return FloatBarMenu.TextQuickItems.slice(0, -1);
    }
    return FloatBarMenu.TableQuickItems;
}

/**
 * 获取图表快捷菜单
 * @param model uiModel
 */
function getChartQuickSettingMenuItems(model: IChartModel): IMenuItem[] {
    return FloatBarMenu.ChartQuickItems;
}

/**
 * 获取 SmartArt 快捷菜单
 * @param model uiModel
 */
function getSmartArtQuickSettingMenuItems(model: ISmartArtModel): IMenuItem[] {
    return FloatBarMenu.SmartArtQuickItems;
}

function getGroupQuickSettingMenuItems(model: IGroupModel): IMenuItem[] {
    if (getAllImage(model).length) {
        // @ts-ignore
        return isMediaContent(model)
            ? FloatBarMenu.GroupQuickItemWithMediaAndImage
            : FloatBarMenu.GroupQuickItemsWithImage;
    }
    return isMediaContent(model) ? FloatBarMenu.GroupQuickItemWithMedia : FloatBarMenu.GroupQuickItems;
}

function getMultiQuickSettingMenuItems(model: IMultiplyModel): IMenuItem[] {
    const models = getAllImage(model);
    if (models?.length) {
        if (models.some(child => [UIModelType.IMAGE, UIModelType.SHAPE].includes(child.type)
            && child.parent?.type !== UIModelType.GROUP)) {
            // @ts-ignore
            return FloatBarMenu.FloatToolbarWithAtLeastOneImageItems;
        }
        // @ts-ignore
        return FloatBarMenu.FloatToolbarWithImageItems;
    }
    return FloatBarMenu.FloatToolbarsItems;
}

function getAddonManager() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.addonManager;
}

function getAIController() {
    return (AppServiceProvider.getInstance().getAppServices() as BaseEditorAppServices)
        .editableServiceLayer?.aiController;
}
